/**
 * AppointmentStepsPill.js
 * Etapas do atendimento no drawer, ao lado da pill de status: mostra x/y (ou
 * "Pronto para chamar") e abre o checklist que a empresa cadastrou para o
 * status atual (Cadastros > Etapas do atendimento). Sem etapas para o status,
 * não renderiza.
 */

import React, { useEffect, useRef, useState } from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { stepProgressFor, readyLabelFor } from '../../../../appointment-steps/appointmentSteps';
import useAppointmentSteps from '../../../../appointment-steps/useAppointmentSteps';
import StepsProgressRing from '../../../../appointment-steps/components/StepsProgressRing';
import StepsChecklist from '../../../../appointment-steps/components/StepsChecklist';
import panelStyles from './AppointmentDetailPanel.module.css';
import styles from './AppointmentStepsPill.module.css';

const ChevronDownIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

function AppointmentStepsPill({ appointment, onChecksChange }) {
  const steps = useAppointmentSteps();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Fecha ao clicar fora (como a pill de status) ou com Esc
  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const progress = stepProgressFor(appointment, steps);
  if (!appointment?.id || !progress) return null;

  const useRequired = progress.totalRequired > 0;
  const done = useRequired ? progress.doneRequired : progress.done;
  const total = useRequired ? progress.totalRequired : progress.total;

  return (
    <div className={`${panelStyles.statusPillWrapper} ${styles.wrapper}`} ref={wrapperRef}>
      <button
        type="button"
        className={`${panelStyles.statusPill} ${progress.ready ? styles.pillReady : styles.pill}`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        {progress.ready
          ? <CheckCircleRoundedIcon sx={{ fontSize: 15 }} />
          : <StepsProgressRing done={done} total={total} size={15} />}
        <span className={panelStyles.statusPillLabel}>
          {progress.ready ? readyLabelFor(progress.status) : 'Etapas'}
        </span>
        {!progress.ready && <span className={styles.counter}>{done} de {total}</span>}
        <ChevronDownIcon />
      </button>

      {open && (
        <div className={`${panelStyles.statusDropdown} ${styles.dropdown}`}>
          <div className={panelStyles.statusDropdownTitle}>
            {useRequired ? `Etapas · ${done} de ${total} obrigatórias` : `Etapas · ${done} de ${total}`}
          </div>
          <StepsChecklist appointment={appointment} progress={progress} onChecksChange={onChecksChange} />
        </div>
      )}
    </div>
  );
}

export default AppointmentStepsPill;
